/**
 * ScheduleStep — the Create Workshop wizard step for date and start, typed or
 * picked, then the cadence controls. There is no End field: a workshop runs for
 * its type's length. Venue lives on the Location step.
 *
 * Extracted from workshopCreateSteps so each file declares one component
 * (`react/no-multi-comp`); behaviour is unchanged.
 */
import { formatTimeLabel } from '../../primitives/date-time-input';
import { DateField } from '../../primitives/DateField';
import { TimeField } from '../../primitives/TimeField';
import { TWO_COLUMN_GRID } from '../../primitives/two-column-grid';
import { Field } from '../../venues/venueCreateFields';

import { CadenceBlock } from './CadenceBlock';
import type { WorkshopCreateForm } from './useWorkshopCreateForm';
import { WorkshopDateWarning } from './WorkshopDateWarning';
import { formatDuration } from './workshopTypeDefaults';

const COL: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };

/** "8:00 PM · 2 hours": the end the type's length gives, or what is missing. */
function endsLabel(end: string, durationMinutes: number | null): string {
  if (durationMinutes === null) return '—';
  const length = formatDuration(durationMinutes);
  return end ? `${formatTimeLabel(end)} · ${length}` : `${length} after the start`;
}

/** Schedule step (round 8) — date and start, with the end shown from the
 *  type's length, then the recurrence controls. Venue moved to the Location
 *  step. */
export function ScheduleStep({ form }: { form: WorkshopCreateForm }) {
  const { draft, fieldError, editSchedule, dateError, dateWarning, minDate } = form;
  return (
    <div style={COL}>
      <Field label="Date" required error={dateError ?? fieldError('startsAt')}>
        <DateField
          ariaLabel="Date"
          min={minDate}
          error={!!(dateError || fieldError('startsAt'))}
          value={draft.schedule.date}
          onChange={(v) => editSchedule('date', v)}
        />
      </Field>
      {/* Non-blocking (BON-508): a near-term date is allowed, just flagged. */}
      <WorkshopDateWarning message={dateWarning} />
      <div style={TWO_COLUMN_GRID}>
        <Field label="Start" required error={fieldError('start')}>
          <TimeField
            ariaLabel="Start"
            error={!!fieldError('start')}
            value={draft.schedule.start}
            onChange={(v) => editSchedule('start', v)}
          />
        </Field>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nb-ink-2)' }}>Ends</span>
          <span
            data-testid="schedule-ends"
            aria-live="polite"
            style={{ minHeight: 32, display: 'flex', alignItems: 'center', fontSize: 12.5 }}
          >
            {endsLabel(draft.schedule.end, draft.workshop.durationMinutes)}
          </span>
          {fieldError('endsAt') && (
            <span style={{ fontSize: 10.5, color: 'var(--nb-risk)' }}>{fieldError('endsAt')}</span>
          )}
        </div>
      </div>
      <CadenceBlock form={form} />
    </div>
  );
}
